import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components';

interface AgentTestOverlayProps {
  isOpen: boolean;
  isConnected: boolean;
  isSpeaking: boolean;
  onClose: () => void;
}

export const AgentTestOverlay: React.FC<AgentTestOverlayProps> = ({
  isOpen,
  isConnected,
  isSpeaking,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-[500px] mx-4 border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="material-icons text-cyan-500">mic</span>
            {t('agents.webTestActiveTitle')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* AI Wave Animation */}
        <div className="mb-6 h-32 flex items-center justify-center bg-gradient-to-br from-cyan-50 to-purple-50 dark:from-gray-900/50 dark:to-gray-800/50 rounded-xl overflow-hidden relative">
          {isConnected ? (
            <div className="flex gap-1 items-center justify-center h-full">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="wave-bar"
                  data-speaking={isSpeaking}
                  style={{
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="flex gap-1 items-center justify-center h-full">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-8 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"
                  style={{
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Info Text */}
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="material-icons text-amber-600 dark:text-amber-400 text-sm mt-0.5">
              info
            </span>
            <div className="text-sm text-amber-900 dark:text-amber-100">
              <strong className="block mb-1">{t('agents.webTestNotice')}</strong>
              <p className="text-amber-800 dark:text-amber-200">{t('agents.webTestDescription')}</p>
            </div>
          </div>
        </div>

        {/* End Test Button */}
        <Button
          variant="danger"
          size="lg"
          fullWidth
          onClick={onClose}
          leftIcon={<span className="material-icons">stop</span>}
        >
          {t('agents.webTestEndButton')}
        </Button>

        {/* Connection Status */}
        <div className="mt-4 flex items-center justify-center gap-2 text-sm">
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-500 animate-pulse' : 'bg-yellow-500 animate-pulse'
            }`}
          />
          <span className="text-gray-600 dark:text-gray-400">
            {isConnected ? t('agents.testSessionConnected') : 'Connecting...'}
          </span>
        </div>
      </div>

      {/* CSS Animation Styles */}
      <style>{`
        @keyframes wave {
          0%, 100% {
            height: 2rem;
          }
          50% {
            height: 6rem;
          }
        }

        .wave-bar {
          width: 0.5rem;
          background: linear-gradient(to top, rgb(6, 182, 212), rgb(168, 85, 247));
          border-radius: 9999px;
          height: 2rem;
          transition: height 0.3s ease;
        }

        .wave-bar[data-speaking="true"] {
          animation: wave 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
